import { css, cx } from '@emotion/css';
import Multiselect from 'multiselect-react-dropdown';
import type { FC, MouseEvent } from 'react';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { MotifComponent } from '../../motif/motifConstants';
import { useMotifStyles } from '../../motif/motifReactUtils';
import { Icon } from '../Icon';
import type { MultiSelectProps } from './MultiSelect.types';
import {
  multiSelectArrowCss,
  multiSelectCloseIconCss,
  multiSelectDropdownBaseCss,
  multiSelectOverflowChipCss,
  multiSelectWrapperCss,
} from './styles';

/**
 * Generic multi-select dropdown wrapping multiselect-react-dropdown.
 *
 * Provides structural layout (wrapper, arrow icon, black cross chip remove icon).
 */
export const MultiSelect: FC<MultiSelectProps> = ({
  options,
  selectedValues,
  displayValue = 'name',
  placeholder,
  showCheckbox,
  hidePlaceholder = true,
  avoidHighlightFirstOption = true,
  onSelect,
  onRemove,
  onKeyPressFn,
  dropdownClassName,
  wrapperClassName,
  arrowClassName,
  maxVisibleChips,
}) => {
  useMotifStyles(MotifComponent.MULTI_SELECT);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);

  const closeIcon = useMemo(() => <Icon name="cross" className={multiSelectCloseIconCss} />, []);

  useLayoutEffect(() => {
    setPortalTarget(wrapperRef.current?.querySelector('.searchWrapper') ?? null);
  }, []);

  const selectedCount = selectedValues?.length ?? 0;
  const shouldTruncate = maxVisibleChips != null && selectedCount > maxVisibleChips;
  const overflowCount = shouldTruncate ? selectedCount - maxVisibleChips : 0;

  const overflowCss = useMemo(() => {
    if (!shouldTruncate) return undefined;
    return css`
      .searchWrapper .chip:nth-child(n + ${maxVisibleChips! + 1}):not([data-overflow]) {
        display: none;
      }
      .searchWrapper input {
        order: 1;
      }
    `;
  }, [shouldTruncate, maxVisibleChips]);

  const handleArrowMouseDown = useCallback((event: MouseEvent) => {
    event.preventDefault();
  }, []);

  const handleArrowClick = useCallback(() => {
    const input = wrapperRef.current?.querySelector<HTMLInputElement>('.searchWrapper input');
    if (!input) return;

    if (document.activeElement === input) {
      input.blur();
    } else {
      input.focus();
    }
  }, []);

  const handleOverflowMouseDown = useCallback((event: MouseEvent) => {
    event.preventDefault();
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={cx(MotifComponent.MULTI_SELECT, multiSelectWrapperCss, wrapperClassName)}
    >
      <Multiselect
        options={options}
        selectedValues={selectedValues}
        displayValue={displayValue}
        placeholder={placeholder}
        showCheckbox={showCheckbox}
        hidePlaceholder={hidePlaceholder}
        avoidHighlightFirstOption={avoidHighlightFirstOption}
        onSelect={onSelect}
        onRemove={onRemove}
        onKeyPressFn={onKeyPressFn}
        className={cx(multiSelectDropdownBaseCss, dropdownClassName, overflowCss)}
        customCloseIcon={closeIcon}
      />
      {shouldTruncate &&
        portalTarget &&
        createPortal(
          <span
            className={cx('chip', multiSelectOverflowChipCss)}
            data-overflow
            onMouseDown={handleOverflowMouseDown}
          >
            +{overflowCount} more
          </span>,
          portalTarget
        )}
      <button
        type="button"
        aria-label="Toggle dropdown"
        className={cx(multiSelectArrowCss, arrowClassName)}
        onMouseDown={handleArrowMouseDown}
        onClick={handleArrowClick}
      >
        <Icon name="chevron-down" />
      </button>
    </div>
  );
};

MultiSelect.displayName = 'MultiSelect';
